import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMagnifyingGlass, faFileExcel, faSpinner, faChevronDown, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { TableProps, Column } from './types';
import * as S from './styles';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

function Table<T extends Record<string, any>>({
  data,
  columns,
  actions,
  exportable = false,
  pagination = {
    itemsPerPage: 50,
    hasMore: false,
  },
  expandable,
}: TableProps<T>) {
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState<Record<string, string>>({});
  const [visibleData, setVisibleData] = useState<T[]>([]);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});
  const [expandedRowsData, setExpandedRowsData] = useState<Record<string, any[]>>({});
  const [expandedRowsLoading, setExpandedRowsLoading] = useState<Record<string, boolean>>({});
  const [headerHeight, setHeaderHeight] = useState(49); // Altura padrão inicial do cabeçalho
  const tableRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLTableSectionElement>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const loadingRef = useRef<HTMLDivElement>(null);

  // Efeito para calcular a altura real do cabeçalho (considerando filtros)
  useEffect(() => {
    if (headerRef.current) {
      const height = headerRef.current.getBoundingClientRect().height;
      setHeaderHeight(height);
    }
  }, [showFilters]);

  const handleFilterChange = (columnKey: string, value: string) => {
    setFilters((prev) => ({
      ...prev,
      [columnKey]: value,
    }));
    // Resetar a paginação quando os filtros mudam
    setPage(1);
  };

  // Função para expandir ou colapsar uma linha
  const toggleRowExpand = async (item: T, index: number) => {
    const rowId = `${index}-${String(item[Object.keys(item)[0]])}`;

    // Inverte o estado de expansão da linha
    const newExpandedRows = {
      ...expandedRows,
      [rowId]: !expandedRows[rowId]
    };
    setExpandedRows(newExpandedRows);

    // Se a linha está sendo expandida e não temos dados ainda, busca os dados
    if (newExpandedRows[rowId] && !expandedRowsData[rowId] && expandable?.fetchChildData) {
      setExpandedRowsLoading({
        ...expandedRowsLoading,
        [rowId]: true
      });

      try {
        const childData = await expandable.fetchChildData(item);
        setExpandedRowsData({
          ...expandedRowsData,
          [rowId]: childData
        });
      } catch (error) {
        console.error('Erro ao buscar dados da subtabela:', error);
      } finally {
        setExpandedRowsLoading({
          ...expandedRowsLoading,
          [rowId]: false
        });
      }
    }
  };

  /**
   * Exporta os dados da tabela para um arquivo Excel
   *
   * Comportamento:
   * - Exporta todas as colunas da tabela exceto as colunas 'actions' e 'index'
   * - Prioriza o conteúdo do td (renderização personalizada) sobre o render e o key
   * - Se o conteúdo for um objeto React, usa o valor original da propriedade
   * - O nome do arquivo inclui a data atual
   */
  const handleExportExcel = () => {
    if (data.length === 0) {
      alert('Não há dados para exportar');
      return;
    }

    try {
      // Preparar os dados para exportação
      const dataToExport = data.map((item: T) => {
        const exportRow: Record<string, any> = {};

        // Processar cada coluna
        columns.forEach((column) => {
          // Ignora colunas de ação
          if (column.key === 'actions' || !column.key || column.key === 'index') {
            return;
          }

          // Determinar o valor da célula
          let cellValue;

          // Prioriza o td (renderização personalizada) se existir
          if (column.td) {
            const renderedValue = column.td(item, column);
            if (renderedValue && typeof renderedValue !== 'object') {
              cellValue = renderedValue;
            } else {
              // Se o valor renderizado for um objeto React, usar o valor original
              cellValue = item[column.key as keyof T];
            }
          }
          // Usar render se disponível e não houver td
          else if (column.render) {
            const renderedValue = column.render(item);
            if (renderedValue && typeof renderedValue !== 'object') {
              cellValue = renderedValue;
            } else {
              cellValue = item[column.key as keyof T];
            }
          }
          // Usar o valor original da propriedade
          else {
            cellValue = item[column.key as keyof T];
          }

          // Adicionar ao objeto de exportação
          exportRow[column.header] = cellValue;
        });

        return exportRow;
      });

      // Criar planilha Excel
      const worksheet = XLSX.utils.json_to_sheet(dataToExport);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Dados');

      // Converter e salvar o arquivo
      const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([excelBuffer], { type: 'application/octet-stream' });
      const fileName = `exportacao_${new Date().toISOString().split('T')[0]}.xlsx`;
      saveAs(blob, fileName);
    } catch (error) {
      console.error('Erro ao exportar para Excel:', error);
      alert('Erro ao exportar para Excel');
    }
  };

  const filteredData = useMemo(() => {
    return data.filter((item) => {
      return Object.entries(filters).every(([key, filterValue]) => {
        if (!filterValue) return true;

        const columnValue = item[key];
        if (columnValue === undefined) return true;

        const filterValueArray = filterValue.toLowerCase().split(' ');

        for (const value of filterValueArray) {
          if (!String(columnValue).toLowerCase().includes(value)) {
            return false;
          }
        }
        return true;
      });
    });
  }, [data, filters]);

  // Efeito para carregar mais itens quando a página muda
  useEffect(() => {
    const start = 0;
    const end = Math.min(page * (pagination?.itemsPerPage || 50), filteredData.length);
    setVisibleData(filteredData.slice(start, end));
  }, [filteredData, page, pagination?.itemsPerPage]);

  // Callback para carregar mais itens
  const loadMoreItems = useCallback(() => {
    const hasMoreItems = visibleData.length < filteredData.length;

    if (!loading && hasMoreItems) {
      setLoading(true);
      // Simula um atraso para evitar múltiplas chamadas
      setTimeout(() => {
        setPage(prevPage => prevPage + 1);
        setLoading(false);
      }, 300);
    }
  }, [filteredData.length, loading, visibleData.length]);

  // Configurar o IntersectionObserver para detectar quando o usuário chega ao fim da tabela
  useEffect(() => {
    if (loadingRef.current) {
      observerRef.current = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting) {
            loadMoreItems();
          }
        },
        { threshold: 0.1 }
      );

      observerRef.current.observe(loadingRef.current);
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [loadMoreItems]);

  const allColumns: Column<T>[] = useMemo(
    () => {
      const baseColumns: Column<T>[] = [
        {
          key: 'index' as keyof T | 'index' | 'actions',
          header: '#',
          width: '50px',
        },
      ];

      // Adiciona a coluna de expansão se a tabela for expansível
      if (expandable?.enabled) {
        baseColumns.unshift({
          key: 'expand' as keyof T | 'index' | 'actions',
          header: '',
          width: '40px',
          td: (item: T, _: Column<T>) => null // Será preenchido no renderCell
        } as Column<T>);
      }

      return [...baseColumns, ...columns];
    },
    [columns, expandable?.enabled]
  );

  const renderCell = (item: T, column: Column<T>, index: number) => {
    // Renderiza o ícone de expansão se for a coluna de expansão
    if (column.key === 'expand' && expandable?.enabled) {
      const rowId = `${index}-${String(item[Object.keys(item)[0]])}`;
      return (
        <S.ExpandButton
          onClick={() => toggleRowExpand(item, index)}
          title={expandedRows[rowId] ? "Ocultar detalhes" : "Mostrar detalhes"}
        >
          {expandedRowsLoading[rowId] ? (
            <FontAwesomeIcon icon={faSpinner} spin />
          ) : (
            <FontAwesomeIcon icon={expandedRows[rowId] ? faChevronDown : faChevronRight} />
          )}
        </S.ExpandButton>
      );
    }

    if (column.td) {
      return column.td(item, column);
    }

    if (column.key === 'actions') {
      return (
        <S.ActionContainer>
          {actions?.map((action, actionIndex) => (
            <S.IconButton
              key={actionIndex}
              onClick={() => action.onClick(item)}
              title={action.title}
            >
              <FontAwesomeIcon icon={action.icon} />
            </S.IconButton>
          ))}
        </S.ActionContainer>
      );
    }

    if (column.render) return column.render(item);
    if (column.key === 'index') return `#${index + 1}`;
    return column.key ? item[column.key as keyof T] : null;
  };

  // Renderiza a linha expandida com os dados da subtabela
  const renderExpandedRow = (item: T, index: number) => {
    const rowId = `${index}-${String(item[Object.keys(item)[0]])}`;

    if (!expandedRows[rowId]) return null;

    // Se tem um renderizador personalizado, usa ele
    if (expandable?.renderExpandedRow) {
      return (
        <tr>
          <td colSpan={allColumns.length}>
            <S.ExpandedContent>
              {expandable.renderExpandedRow(item)}
            </S.ExpandedContent>
          </td>
        </tr>
      );
    }

    // Se está carregando os dados
    if (expandedRowsLoading[rowId]) {
      return (
        <tr>
          <td colSpan={allColumns.length}>
            <S.LoadingContainer>
              <FontAwesomeIcon icon={faSpinner} spin />
              <span>Carregando dados...</span>
            </S.LoadingContainer>
          </td>
        </tr>
      );
    }

    // Se tem dados e colunas para a subtabela
    if (expandedRowsData[rowId]?.length > 0 && expandable?.childColumns) {
      return (
        <tr>
          <td colSpan={allColumns.length}>
            <S.ExpandedContent>
              <S.SubTable>
                <thead>
                  <tr>
                    {expandable.childColumns.map((column, colIndex) => (
                      <S.TableHeader key={`child-header-${colIndex}`}>
                        {column.header}
                      </S.TableHeader>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {expandedRowsData[rowId].map((childItem, childIndex) => (
                    <S.TableRow key={`child-row-${childIndex}`}>
                      {expandable.childColumns?.map((childColumn, childColIndex) => (
                        <S.TableCell key={`child-cell-${childIndex}-${childColIndex}`}>
                          {childColumn.td
                            ? childColumn.td(childItem, childColumn)
                            : childColumn.render
                              ? childColumn.render(childItem)
                              : childItem[childColumn.key as keyof typeof childItem]}
                        </S.TableCell>
                      ))}
                    </S.TableRow>
                  ))}
                </tbody>
              </S.SubTable>
            </S.ExpandedContent>
          </td>
        </tr>
      );
    }

    // Se não tem dados na subtabela
    if (expandedRowsData[rowId]?.length === 0) {
      return (
        <tr>
          <td colSpan={allColumns.length}>
            <S.ExpandedContent>
              <p>Nenhum dado encontrado.</p>
            </S.ExpandedContent>
          </td>
        </tr>
      );
    }

    return null;
  };

  const hasMoreItems = visibleData.length < filteredData.length;

  return (
    <>
      <S.TableContainer ref={tableRef}>
        <S.Table>
          <S.TableHead ref={headerRef}>
            <tr>
              {allColumns.map((column, index) => (
                <S.TableHeader
                  key={String(column.key)}
                  style={{ width: column.width }}
                >
                  {index === (expandable?.enabled ? 1 : 0) ? (
                    <S.HeaderContent
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        textAlign: 'center',
                        justifyContent: 'space-between',
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        {column.header}
                        <S.FilterIcon
                          onClick={() => setShowFilters(!showFilters)}
                          active={showFilters}
                        >
                          <FontAwesomeIcon icon={faMagnifyingGlass} />
                        </S.FilterIcon>
                      </div>
                      {exportable && (
                        <S.ExportIcon onClick={handleExportExcel} title="Exportar para Excel">
                          <FontAwesomeIcon icon={faFileExcel} />
                        </S.ExportIcon>
                      )}
                    </S.HeaderContent>
                  ) : (
                    column.header
                  )}
                </S.TableHeader>
              ))}
            </tr>
            {showFilters && (
              <tr>
                {allColumns.map((column) => (
                  <S.TableHeader key={`filter-${String(column.key)}`}>
                    {column.filterable &&
                      column.key !== 'actions' &&
                      column.key !== 'index' &&
                      column.key !== 'expand' && (
                        <S.FilterInput
                          type="text"
                          value={filters[column.key as string] || ''}
                          onChange={(e) =>
                            handleFilterChange(
                              column.key as string,
                              e.target.value
                            )
                          }
                          placeholder={`Filtrar ${column.header.toLowerCase()}...`}
                        />
                      )}
                  </S.TableHeader>
                ))}
              </tr>
            )}
          </S.TableHead>
          {visibleData.length > 0 ? (
            <tbody>
              {visibleData.map((item, index) => (
                <React.Fragment key={`row-${index}`}>
                  <S.TableRow
                    active={expandedRows[`${index}-${String(item[Object.keys(item)[0]])}`]}
                    isSticky={expandedRows[`${index}-${String(item[Object.keys(item)[0]])}`]}
                    style={{
                      top: expandedRows[`${index}-${String(item[Object.keys(item)[0]])}`] ? `${headerHeight}px` : 'auto'
                    }}
                  >
                    {allColumns.map((column) => (
                      <S.TableCell key={`${index}-${String(column.key)}`}>
                        {renderCell(item, column, index)}
                      </S.TableCell>
                    ))}
                  </S.TableRow>
                  {renderExpandedRow(item, index)}
                </React.Fragment>
              ))}
              {hasMoreItems && (
                <tr>
                  <td colSpan={allColumns.length}>
                    <S.LoadingContainer ref={loadingRef}>
                      {loading && <FontAwesomeIcon icon={faSpinner} spin />}
                    </S.LoadingContainer>
                  </td>
                </tr>
              )}
            </tbody>
          ) : (
            <tbody>
              <S.TableRow key={'sem-registros'}>
                <S.TableCell
                  colSpan={allColumns.length}
                  style={{ textAlign: 'center' }}
                >
                  {Object.keys(filters).some((key) => filters[key])
                    ? 'Nenhum registro encontrado para os filtros aplicados'
                    : 'Nenhum registro encontrado'}
                </S.TableCell>
              </S.TableRow>
            </tbody>
          )}
        </S.Table>
      </S.TableContainer>
    </>
  );
}

export default Table;
